/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, Input} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {IndustryScenario} from "../../../models/industry-scenario-dto.model";
import {ExcelExportService} from '../../../services/excel-export.service';
import {IndustryScenarioService} from '../../../services/industry-scenario.service';
import {ColumnHeader} from '../../models/column-header.model';
import {WorksheetExportRequest} from '../../models/worksheet-export-request.model';
import {StateService} from '../../state/state.service';

@Component({
    selector: 'app-update-scenario-with-segment-scheme',
    templateUrl: './update-scenario-with-segment-scheme.component.html',
    styleUrls: ['./update-scenario-with-segment-scheme.component.scss'],
    standalone: false
})
export class UpdateScenarioWithSegmentSchemeComponent {
  @Input() industryScenario: IndustryScenario;
  @Input() disabled: boolean;
  @Input() isMenuItem: boolean;

  showSpinner: boolean;

  updateResults: any[] = [];
  updateSegmentSchemeSheetName = 'SGMNT_SCHM';
  updateReportColumnHeaders: ColumnHeader[] = [
    {name: 'SGMNT_SCHM', label: 'SGMNT_SCHM'},
    {name: 'SGMNT', label: 'SGMNT'},
  ];

  constructor(public snackBar: FabMessageService,
              public dialog: MatDialog,
              readonly industryScenarioService: IndustryScenarioService,
              readonly stateService: StateService,
              private excelService: ExcelExportService) {
  }

  /**
   * Creates the export request for the data that is returned from updating an industry scenario with segment scheme
   */
  createExportRequest(): WorksheetExportRequest {
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const filename = 'SegmentUpdates_' + this.industryScenario.id + "_" + currDate;
    const data = new Map<string, any>();
    data[this.updateSegmentSchemeSheetName] = this.updateResults;
    const request = this.excelService.createExportRequestBody(filename,
      data,
      [[this.updateSegmentSchemeSheetName, this.updateReportColumnHeaders]]);
    return request;
  }

  /**
   * Export the results that are returned from the Update with Segment Scheme
   */
  export(exportRequest: WorksheetExportRequest): void {
    this.showSpinner = true;
    this.excelService.transformToExcel(exportRequest).subscribe(
      (file: any) => {
        this.excelService.exportToExcelFromServer(file, exportRequest.fileName);
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      }, () => {
        this.showSpinner = false;
      });
  }

  /**
   * Make service call to update the industry scenario with segment scheme
   */
  updateIndustryScenarioWithSegmentScheme(): void {
    this.showSpinner = true;
    this.industryScenarioService.updateIndustryScenarioWithSegmentScheme(this.stateService.getUserGmid(),
      this.industryScenario.id).subscribe(
      data => {
        this.updateResults = data;
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      },
      () => {
        // Export the data if there were results form the update
        if (this.updateResults.length > 0) {
          this.export(this.createExportRequest());
        }
        this.snackBar.displaySuccess('Successfully updated industry scenario with segment scheme', DISMISS_MSG);
        this.showSpinner = false;
      }
    );
  }

  /**
   * Show Confirmation Dialog to update the scenario with segment scheme.
   */
  showConfirmationDialog(): void {
    const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Update Industry Scenario with Segment Scheme',
        message: 'Do you want to update the scenario "' + this.industryScenario.industryScenarioName + '" with segment scheme?',
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'help',
        actionColor: 'primary'
      },
      disableClose: true
    });

    dialogRef.componentInstance.yes.subscribe(() => {
      this.updateIndustryScenarioWithSegmentScheme();
    });
  }
}
